<template>
  <table class="flex flex-col">
    <tbody :style="styleObject">
      <VueDraggableNext v-model="localPicklistOptions" handle=".drag-handle" class="w-full">
        <tr v-for="(pickerOption, index) in localPicklistOptions" :key="pickerOption.id">
          <td v-if="isActive" class="flex items-center justify-center">
            <div class="mr-2 mt-1.5 h-8">
              <Menu05Icon
                class="drag-handle ml-4 mt-1 h-4 w-4 flex-shrink-0 cursor-move text-gray-500"
              />
            </div>
          </td>
          <td>
            <label
              :id="`label-${pickerOption.id}`"
              :key="resetKey"
              :ref="(el) => setLabelRef(el as HTMLElement, index)"
              :aria-labelledby="`label-${pickerOption.id}`"
              :for="`label-${pickerOption.id}-${index}`"
              contenteditable="true"
              class="inline-block w-auto cursor-text border-0 shadow-none outline-none"
              @input="
                (e) => {
                  handleLabelInput(e, index);
                }
              "
              @compositionstart="handleCompositionStart"
              @compositionend="
                (e) => {
                  compositionLabelEndHandler(e, index);
                }
              "
              @keydown="handleSafariCursor"
              @keydown.enter.prevent.stop="addOptionAt(index)"
              @keyup.tab.prevent.stop
              @keydown.tab="(e) => handleTextboxTabFromLabel(e, index)"
              @keyup.enter.prevent.stop
              @focus="handleLabelFocus"
              @blur="(e) => e.stopPropagation()"
              @click="(e) => e.stopPropagation()"
              >{{ pickerOption.label }}</label
            >
          </td>
          <td>
            <div class="option col-12 mb-2 pl-2" @click="(e) => e.stopPropagation()">
              <input
                v-if="type === 'input'"
                :id="pickerOption.id"
                :ref="(el) => setInputRef(el as HTMLInputElement, index)"
                v-model="pickerOption.prefillValue"
                :aria-labelledby="`label-${pickerOption.id}`"
                :class="classList"
                :style="styleObject"
                type="text"
                class="form-control"
                :placeholder="t('common.fillValuesPlaceholder')"
                @focus="(e) => e.stopPropagation()"
                @blur="(e) => e.stopPropagation()"
                @keydown.tab="(e) => handleTextboxTabFromInput(e, index)"
              />
            </div>
          </td>
          <td>
            <Trash01Icon
              v-if="isActive && !isCustomObject"
              class="ml-2 mt-[-0.5rem] h-4 w-4 flex-shrink-0 cursor-pointer text-gray-500"
              @click="prepareToRemoveOption(index)"
            />
          </td>
        </tr>
      </VueDraggableNext>
    </tbody>
    <div v-if="isActive" class="mt-[-1rem] flex items-center">
      <UIButton
        id="textbox-list-add-option"
        text
        class="mt-4"
        :disabled="isLastOptionEmpty"
        @click="addOption"
      >
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("common.addOption") }}
      </UIButton>
    </div>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleInputShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @keydown="onShortLabelKeydown"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">
        {{ element.shortLabel }}
      </span>
    </template>
  </table>
  <DeleteConfirmationModal
    :show="showDeleteModal"
    @confirm="removeOption"
    @cancel="cancelRemoveOption"
  />
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { isCustomObjectField } from "@/util/customObjectUtils";
import {
  handleShortLabelTabNav,
  handleTextboxTabFromInput,
  handleTextboxTabFromLabel,
} from "@/util/focusNavigation";
import {
  fixSafariCursor,
  getCaretOffsetWithin,
  handleShortLabelInput as handleShortLabelInputUtil,
  setCaretOffsetWithin,
} from "@/util/methods";
import { Menu05Icon, PlusIcon, Trash01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton } from "@gohighlevel/ghl-ui";
import { computed, nextTick, PropType, ref, watch } from "vue";
import { VueDraggableNext } from "vue-draggable-next";
import { useI18n } from "vue-i18n";
import DeleteConfirmationModal from "./DeleteConfirmationModal.vue";

const { t } = useI18n();

const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();

const props = defineProps({
  type: {
    type: String,
    default: "input",
  },
  name: {
    type: String,
    default: "",
  },
  value: {
    type: [Array, String],
    default: "",
  },
  currentLocationId: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  styleObject: {
    type: Object,
    default: () => {
      return {
        fontSize: "14px",
      };
    },
  },
  customClass: {
    type: String,
    default: "",
  },
  picklistOptions: {
    type: Array as PropType<Array<any>>,
    default() {
      return [{ id: "test", label: "Santosh", prefillValue: "hgdsjhf" }];
    },
  },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isTW: {
    type: Boolean,
    default: false,
  },
  element: {
    type: Object,
    default: () => {
      return {};
    },
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  customFieldCreated: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:shortLabel", "update:picklistOptions"]);

const shortLabel = ref(props.element.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const indexToRemove = ref<number | null>(null);
const showDeleteModal = ref(false);

const localPicklistOptions = ref([...props.picklistOptions]);

const isInternalUpdate = ref(false);
// Keep refs for labels to navigate with Tab/Shift+Tab
const labelRefs = ref<HTMLElement[]>([]);
const setLabelRef = (el: HTMLElement | null, index: number) => {
  if (el) {
    labelRefs.value[index] = el;
  }
};
const getLabelRef = (i: number): HTMLElement | null => labelRefs.value?.[i] ?? null;
const inputRefs = ref<HTMLInputElement[]>([]);
const setInputRef = (el: HTMLInputElement | null, index: number) => {
  if (el) {
    inputRefs.value[index] = el;
  }
};
const getInputRef = (i: number): HTMLInputElement | null => inputRefs.value?.[i] ?? null;
const isEditing = ref(false);
const isCustomObject = computed(() => isCustomObjectField((props.element as any)?.model));

watch(
  () => props.picklistOptions,
  (newVal) => {
    if (!isInternalUpdate.value && !isEditing.value) {
      localPicklistOptions.value = JSON.parse(JSON.stringify(newVal));
    }
    isInternalUpdate.value = false;
  },
  { deep: true }
);

watch(
  () => localPicklistOptions.value,
  (newVal) => {
    isInternalUpdate.value = true;
    emit("update:picklistOptions", JSON.parse(JSON.stringify(newVal)));
  },
  { deep: true }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const updateOptionLabel = (event: Event, index: number) => {
  isEditing.value = true;
  const target = event.target as HTMLElement;
  event.stopPropagation();
  const caret = getCaretOffsetWithin(target);
  const text = target.innerText.replace(/\u200B/g, "");
  localPicklistOptions.value[index].label = text;
  nextTick(() => {
    isEditing.value = false;
    const el = document.getElementById(`label-${localPicklistOptions.value[index].id}`) || target;
    setCaretOffsetWithin(el as HTMLElement, caret);
  });
};

const handleLabelFocus = (event: FocusEvent) => {
  const target = event.target as HTMLElement;
  event.stopPropagation();

  const selection = window.getSelection();
  selection?.removeAllRanges();

  const sanitized = (target.textContent || "").replace(/\u200B/g, "").trim();
  if (!sanitized) {
    let textNode = target.firstChild as Text | null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      target.textContent = "";
      textNode = document.createTextNode("\u200B");
      target.appendChild(textNode);
    }
    if (!textNode.textContent) {
      textNode.textContent = "\u200B";
    }
    const range = document.createRange();
    range.setStart(textNode, 0);
    range.collapse(true);
    selection?.addRange(range);
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(target);
  selection?.addRange(range);
};

const onShortLabelKeydown = (event: KeyboardEvent) => {
  handleShortLabelTabNav(
    event,
    shortLabelElement.value,
    inputRefs.value,
    labelRefs.value,
    localPicklistOptions.value.length,
    handleSafariCursor
  );
};

const prepareToRemoveOption = (index: number) => {
  if (props.picklistOptions.length > 1) {
    if (props.customFieldCreated) {
      indexToRemove.value = index;
      showDeleteModal.value = true;
    } else {
      const newOptions = [...localPicklistOptions.value];
      newOptions.splice(index, 1);
      localPicklistOptions.value = newOptions;
    }
  }
};

const removeOption = () => {
  if (indexToRemove.value !== null) {
    const newOptions = [...localPicklistOptions.value];
    newOptions.splice(indexToRemove.value, 1);
    localPicklistOptions.value = newOptions;
  }
  showDeleteModal.value = false;
};

const cancelRemoveOption = () => {
  indexToRemove.value = null;
  showDeleteModal.value = false;
};

const isLastOptionEmpty = computed(() => {
  const lastOption = props.picklistOptions[props.picklistOptions.length - 1];
  return lastOption && typeof lastOption.label === "string" && lastOption.label.trim() === "";
});

const addOption = () => {
  addOptionAt((localPicklistOptions.value?.length ?? 1) - 1);
};

const addOptionAt = (index: number) => {
  const newOptions = [...localPicklistOptions.value];
  const insertAt = Math.min(Math.max(index + 1, 0), newOptions.length);
  const newId = `option-${Date.now()}`;
  newOptions.splice(insertAt, 0, {
    id: newId,
    label: `Option ${newOptions.length + 1}`,
    prefillValue: "",
  });
  localPicklistOptions.value = newOptions;
  nextTick(() => {
    const newLabel = document.getElementById(`label-${newId}`);
    if (newLabel) {
      newLabel.focus();
      const range = document.createRange();
      range.selectNodeContents(newLabel);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  });
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const rawLabelInputHandler = (e: Event, index: number) => {
  updateOptionLabel(e, index);
  handleSafariCursor(e);
};

const handleLabelInput = withIMECheck(rawLabelInputHandler);
const compositionLabelEndHandler = handleCompositionEnd(rawLabelInputHandler);

const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style>
table.table.table-borderless th {
  border: 0;
}
table.table.table-borderless tr th:first-child {
  padding-right: 0;
  text-align: right;
  vertical-align: middle;
  max-width: 150px;
}
.textbox-list .option {
  flex: 0 0 100%;
  max-width: 100%;
  padding: 0;
}
.textbox-list label {
  width: 65px;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}
</style>
